/**
 * Reading a manifest well enough to build a form from it.
 *
 * The manifest is validated by the API and by the schema package, so this does not
 * re-validate — it only needs to know enough to render the right control and to send
 * back a value the API will accept.
 */

export type ParamType =
  | "string"
  | "number"
  | "integer"
  | "boolean"
  | "date"
  | "datetime"
  | "enum"
  | "list"
  | "keyvalue"
  | "secret_ref";

export interface ParamSpec {
  name: string;
  type: ParamType;
  label?: string | null;
  description?: string | null;
  required?: boolean;
  default?: unknown;
  options?: string[] | null;
  multiple?: boolean;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  pattern?: string | null;
  item_type?: string;
  arg?: string | null;
}

export interface InputSpec {
  name: string;
  label?: string | null;
  description?: string | null;
  required?: boolean;
  multiple?: boolean;
  accept?: string[] | null;
  max_size_mb?: number | null;
  source?: string[] | null;
}

export interface OutputSpec {
  name: string;
  label?: string | null;
  description?: string | null;
  path: string;
  required?: boolean;
}

export interface Manifest {
  metadata?: { name?: string; description?: string | null };
  params?: ParamSpec[];
  inputs?: InputSpec[];
  outputs?: OutputSpec[];
  validators?: unknown[];
  runtime?: { resources?: { timeout_minutes?: number } };
}

/** What a field should be labelled. Falls back to a readable form of its name. */
export function labelFor(spec: { name: string; label?: string | null }): string {
  if (spec.label) return spec.label;
  return spec.name
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** The starting value for a param, so the form matches what the API would default to. */
export function initialValue(spec: ParamSpec): unknown {
  if (spec.default !== undefined && spec.default !== null) return spec.default;
  if (spec.type === "boolean") return false;
  if (spec.type === "list" || spec.multiple) return [];
  if (spec.type === "keyvalue") return {};
  return "";
}

export function initialValues(params: ParamSpec[]): Record<string, unknown> {
  return Object.fromEntries(params.map((spec) => [spec.name, initialValue(spec)]));
}

/**
 * Drop values the API would reject or ignore.
 *
 * An empty optional field means "not supplied", not "supplied as empty" — sending the
 * empty string would fail a pattern or a number coercion for no reason.
 */
export function prepareParams(
  params: ParamSpec[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const spec of params) {
    const value = values[spec.name];

    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (
      spec.type === "keyvalue" &&
      typeof value === "object" &&
      Object.keys(value as object).length === 0
    ) {
      continue;
    }

    out[spec.name] = value;
  }

  return out;
}

/** Client-side checks, so an obvious mistake does not need a round trip to be caught. */
export function validate(
  params: ParamSpec[],
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const spec of params) {
    const value = values[spec.name];
    const empty =
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);

    if (spec.required && empty && spec.type !== "boolean") {
      errors[spec.name] = "This is required.";
      continue;
    }
    if (empty) continue;

    if (spec.type === "number" || spec.type === "integer") {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) {
        errors[spec.name] = "Enter a number.";
      } else if (spec.type === "integer" && !Number.isInteger(numeric)) {
        errors[spec.name] = "Enter a whole number.";
      } else if (spec.min !== null && spec.min !== undefined && numeric < spec.min) {
        errors[spec.name] = `Must be ${spec.min} or more.`;
      } else if (spec.max !== null && spec.max !== undefined && numeric > spec.max) {
        errors[spec.name] = `Must be ${spec.max} or less.`;
      }
    }

    if (spec.type === "string" && spec.pattern && typeof value === "string") {
      try {
        if (!new RegExp(spec.pattern).test(value)) {
          errors[spec.name] = "This does not match the expected format.";
        }
      } catch {
        // An unsupported pattern is the server's to enforce, not ours to guess at.
      }
    }
  }

  return errors;
}

/** Whether a chosen file is plausible for an input, before anything is uploaded. */
export function checkFile(spec: InputSpec, file: File): string | undefined {
  if (spec.accept?.length) {
    const name = file.name.toLowerCase();
    const ok = spec.accept.some((ext) => name.endsWith(ext.toLowerCase()));
    if (!ok) return `${file.name} is not one of: ${spec.accept.join(", ")}`;
  }
  if (spec.max_size_mb && file.size > spec.max_size_mb * 1024 * 1024) {
    return `${file.name} is larger than the ${spec.max_size_mb} MB limit.`;
  }
  return undefined;
}
